import { capitalize } from 'lodash';
import React from 'react';
import { useIntl } from 'react-intl';
import { Checkbox } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { alarmsDictionary } from 'sonata-static-storage/lib/samples/alarms.samples';
import { MetricAlarmTimeFrameInterface } from 'sonata-static-storage/lib/schemas/common/metricAlarm.schema';

import { SettingsTooltipStyled } from './SettingsTooltip.styles';

import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { Notifications } from '../types';

type Props = {
  alarm: Notifications['alarms'][0];
  alarms: Notifications['alarms'];
  onAddAlarm: (alarm: Notifications['alarms'][0]) => void;
  onSetAlarm: (alarm: Notifications['alarms'][0]) => void;
  user: Record<string, any>;
  userRoles: UserRoleInterface[];
};

function SettingsTooltip({ alarm, alarms: userAlarms, onAddAlarm, onSetAlarm, user, userRoles }: Props) {
  const { formatMessage } = useIntl();

  const getUserAlarmByAlarm = () => {
    let userAlarm = userAlarms.find(a => a.type === alarm.type);
    if (!userAlarm) {
      userAlarm = {
        enabled: true,
        type: alarm.type
      };
      onAddAlarm(userAlarm);
    }
    return userAlarm;
  };

  const handleThresholdChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const userAlarm = getUserAlarmByAlarm();
    onSetAlarm({
      ...userAlarm,
      threshold: parseInt(ev.target.value)
    } as Notifications['alarms'][0]);
  };

  const handleTimeIntervalClick = () => {
    const userAlarm = getUserAlarmByAlarm();
    onSetAlarm({
      ...userAlarm,
      timeFrame: {
        units: alarmsDictionary.timeFrameUnits[0],
        value: alarmsDictionary.timeFrameValues[0],
        ...userAlarm.timeFrame,
        enabled: userAlarm.timeFrame ? !userAlarm.timeFrame.enabled : true
      }
    } as Notifications['alarms'][0]);
  };

  const handleTimeFrameValueChange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const userAlarm = getUserAlarmByAlarm();
    onSetAlarm({
      ...userAlarm,
      timeFrame: {
        ...userAlarm.timeFrame,
        value: parseInt(ev.target.value)
      }
    } as Notifications['alarms'][0]);
  };

  const handleTimeFrameUnitsChange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const userAlarm = getUserAlarmByAlarm();
    onSetAlarm({
      ...userAlarm,
      timeFrame: {
        ...userAlarm.timeFrame,
        units: ev.target.value as unknown as MetricAlarmTimeFrameInterface['units']
      }
    } as Notifications['alarms'][0]);
  };

  const userAlarm = userAlarms.find(a => a.type === alarm.type);
  const alarmDefinition = alarmsDictionary.definitions.find(def => def.type === alarm.type);
  const timeFrameUnitsClass =
    userAlarm && userAlarm.timeFrame && userAlarm.timeFrame.enabled && alarmsDictionary.timeFrameUnits.length > 1
      ? ''
      : 'hide';
  const timeFrameValueClass = userAlarm && userAlarm.timeFrame && userAlarm.timeFrame.enabled ? '' : 'hide';
  const timeFrameLabel =
    alarmsDictionary.timeFrameUnits.length > 1
      ? 'notificationSettings.alarmTimeFrameValue'
      : 'notificationSettings.alarmTimeFrameHoursValue';
  const timeFrameValueCopy = formatMessage({
    defaultMessage: timeFrameLabel,
    id: timeFrameLabel
  });
  const notificationsTimeFramePermission = checkUserResourcePermissions('notificationsWithTimeFrame', user, userRoles);

  return (
    <SettingsTooltipStyled className="settingsTooltip scrollable" data-alarm-type={alarm.type}>
      {!alarmDefinition?.withTimeFrame && !alarmDefinition?.withThreshold ? (
        <div className="settingsTooltip__header settingsTooltip__header--empty">
          {formatMessage({
            defaultMessage: 'Alarm has no settings',
            id: 'menu.notificationSettings.alarmSettingsDisabled'
          })}
        </div>
      ) : (
        <>
          <div className="settingsTooltip__header">
            {formatMessage({
              defaultMessage: 'Trigger settings',
              id: 'menu.notificationSettings.alarmSettings'
            })}
          </div>
          <div className="settingsTooltip__properties">
            {alarmDefinition.withThreshold && (
              <div className="settingsTooltip__threshold">
                <label htmlFor="settingsTooltip__thresholdSelector">
                  {`${formatMessage({
                    defaultMessage: 'Threshold',
                    id: 'menu.notificationSettings.alarmThreshold'
                  })}:`}
                </label>
                <input
                  className="settingsTooltip__thresholdSelector"
                  id="alarmThresholdSelector"
                  min={0}
                  onChange={handleThresholdChange}
                  step="any"
                  type="number"
                  value={(userAlarm && userAlarm.threshold) || 0}
                />
              </div>
            )}
            {notificationsTimeFramePermission && (
              <>
                <div className="settingsTooltip__timeFrame">
                  <label htmlFor="settingsTooltip__timeFrameToggle">
                    {`${formatMessage({
                      defaultMessage: 'Time Interval',
                      id: 'menu.notificationSettings.alarmTimeFrame'
                    })}:`}
                  </label>
                  <Checkbox
                    active={userAlarm && userAlarm.timeFrame ? !!userAlarm.timeFrame.enabled : false}
                    id="alarmSettingsTimeFrameToggle"
                    isSwitch
                    onClickAction={handleTimeIntervalClick}
                  />
                </div>
                <div className={`settingsTooltip__timeFrameUnits ${timeFrameUnitsClass}`}>
                  <label htmlFor="settingsTooltip__timeFrameUnitsSelector">
                    {`${formatMessage({
                      defaultMessage: 'Time Units',
                      id: 'menu.notificationSettings.alarmTimeFrameUnits'
                    })}:`}
                  </label>
                  <select
                    className="settingsTooltip__timeFrameUnitsSelector"
                    defaultValue={
                      userAlarm && userAlarm.timeFrame ? userAlarm.timeFrame.units : alarmsDictionary.timeFrameUnits[0]
                    }
                    onChange={handleTimeFrameUnitsChange}
                  >
                    {alarmsDictionary.timeFrameUnits.map(units => (
                      <option className="timeFrameUnits" key={units} value={units}>
                        {capitalize(units)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={`settingsTooltip__timeFrameValue ${timeFrameValueClass}`}>
                  <label htmlFor="settingsTooltip__timeFrameValueSelector">{`${timeFrameValueCopy}:`}</label>
                  <select
                    className="settingsTooltip__timeFrameValueSelector"
                    onChange={handleTimeFrameValueChange}
                    value={
                      userAlarm && userAlarm.timeFrame ? userAlarm.timeFrame.value : alarmsDictionary.timeFrameValues[0]
                    }
                  >
                    {alarmsDictionary.timeFrameValues.map(value => (
                      <option className="settingsTooltip__timeFrameValue" key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </SettingsTooltipStyled>
  );
}

export default SettingsTooltip;
